import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Patients, isNotFound, fetchPage, cursorOf, mergeById } from "@/lib/api";
import LoadError from "@/components/LoadError";
import LoadMoreButton from "@/components/LoadMoreButton";
import PatientDocuments from "@/components/PatientDocuments";
import { ShieldAlert } from "lucide-react";
import { useAuth } from "@/lib/auth";

const PAGE = 20;
import { Button } from "@/components/ui/button";
import { Stethoscope, ArrowLeft, FileText, Trash2, TrendingUp, ClipboardList, Link2, Link2Off } from "lucide-react";

export default function PatientDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [patient, setPatient] = useState(null);
  const [signStatus, setSignStatus] = useState(null);
  const ost = signStatus?.documents.find((d) => d.key === "consentimento_unificado");
  const ostMissing = ost && (ost.decision === "recusado" || !ost.signed_version || ost.state === "atualizar");
  const [assessments, setAssessments] = useState([]);
  const [loadErr, setLoadErr] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const load = async () => {
    setLoadErr(false);
    try {
      const [p, page] = await Promise.all([Patients.get(id), fetchPage((prm) => Patients.assessments(id, prm), PAGE)]);
      setPatient(p);
      setAssessments(page.items);
      setHasMore(page.more);
    } catch (e) {
      if (!isNotFound(e)) { setLoadErr(true); return; }
      toast.error("Paciente não encontrado");
      nav("/");
    }
  };
  useEffect(() => { load(); }, [id]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const page = await fetchPage((prm) => Patients.assessments(id, prm), PAGE, cursorOf(assessments[assessments.length - 1]));
      setAssessments(prev => mergeById(prev, page.items));
      setHasMore(page.more);
    } catch { toast.error("Falha ao carregar mais avaliações"); }
    finally { setLoadingMore(false); }
  };

  if (loadErr) return <LoadError testId="patient-load-error" onRetry={load} />;
  if (!patient) return <div className="text-slate-500" data-testid="patient-loading">A carregar…</div>;

  const remove = async () => {
    if (!window.confirm("Eliminar este paciente e toda a sua informação?")) return;
    await Patients.remove(id);
    toast.success("Paciente eliminado");
    nav("/");
  };

  return (
    <div className="max-w-5xl mx-auto space-y-10" data-testid="patient-detail-page">
      <Link to="/" className="inline-flex items-center gap-2 text-slate-400 hover:text-slate-200 text-sm" data-testid="back-to-dashboard">
        <ArrowLeft className="w-4 h-4" /> Voltar ao painel
      </Link>
      {ostMissing && (
        <a href="#documentos" data-testid="consent-banner" className="flex items-center gap-2 rounded-xl border border-amber-500/30 bg-amber-500/5 text-amber-200 text-xs px-3 py-2">
          <ShieldAlert className="w-4 h-4 shrink-0" />
          <span className="flex-1 min-w-0">{ost.decision === "recusado" ? "O paciente não autorizou a intervenção osteopática." : ost.state === "atualizar" ? "Consentimento requer atualização para o documento unificado." : "Consentimento e autorizações por assinar."}</span>
          <span className="underline shrink-0">Ver documentos</span>
        </a>
      )}

      <section className="rounded-2xl border border-slate-800 bg-slate-900/40 p-8 backdrop-blur-sm">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-sky-400 font-semibold">Paciente</div>
            <h1 className="font-display text-4xl text-slate-50 mt-2 flex items-center gap-3">
              {patient.name}
              {patient.patient_active === false && (
                <span data-testid="patient-inactive-badge" className="text-[10px] px-2 py-0.5 rounded-full border border-rose-500/40 text-rose-300 bg-rose-500/10 uppercase tracking-wider">Acesso desativado</span>
              )}
            </h1>
            <div className="text-slate-400 mt-1">
              {patient.age != null ? `${patient.age} anos` : "Idade não registada"}
              {patient.gender && patient.gender !== "neutro" && ` · ${patient.gender === "masculino" ? "Masculino" : "Feminino"}`}
              {patient.occupation && ` · ${patient.occupation}`}
              {patient.email && ` · ${patient.email}`}
            </div>
          </div>
          <div className="flex gap-3 flex-wrap">
            <Button
              data-testid="new-assessment-btn"
              onClick={() => nav(`/patients/${id}/assessments/new`)}
              className="bg-sky-500 hover:bg-sky-400 text-slate-950 rounded-full px-6 glow-sky"
            >
              <Stethoscope className="w-4 h-4 mr-2" /> Nova Avaliação
            </Button>
            <Button
              data-testid="intake-btn"
              variant="outline"
              onClick={() => nav(`/patients/${id}/intake`)}
              className="rounded-full border-slate-700 text-slate-200 hover:bg-slate-800"
            >
              <ClipboardList className="w-4 h-4 mr-2" /> Anamnese
            </Button>
            <Button
              data-testid="toggle-patient-active-btn"
              variant="outline"
              onClick={async () => {
                try {
                  const next = !patient.patient_active;
                  const updated = await Patients.setActive(id, next);
                  setPatient(updated);
                  toast.success(next ? "Links do paciente reativados" : "Links do paciente desativados");
                } catch { toast.error("Não foi possível alterar o estado"); }
              }}
              className={`rounded-full ${patient.patient_active === false ? "border-rose-500/40 text-rose-300 hover:bg-rose-500/10" : "border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"}`}
            >
              {patient.patient_active === false ? <Link2Off className="w-4 h-4 mr-2" /> : <Link2 className="w-4 h-4 mr-2" />}
              {patient.patient_active === false ? "Reativar acesso" : "Desativar acesso"}
            </Button>
            {assessments.filter(a => a.analysis).length >= 2 && (
              <Button
                data-testid="compare-btn"
                variant="outline"
                onClick={() => nav(`/patients/${id}/compare`)}
                className="rounded-full border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
              >
                <TrendingUp className="w-4 h-4 mr-2" /> Comparar evolução
              </Button>
            )}
            <Button variant="ghost" className="text-rose-400 hover:bg-rose-500/10 rounded-full" onClick={remove} data-testid="delete-patient-btn">
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Queixa principal</div>
            <p className="text-slate-300 mt-2 whitespace-pre-wrap">{patient.chief_complaint || patient.notes || "—"}</p>
            {patient.pain_locations?.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1.5">
                {patient.pain_locations.map((z, i) => (
                  <span key={i} className="text-[10px] px-2 py-0.5 rounded-full border border-rose-500/30 bg-rose-500/10 text-rose-200">{z}</span>
                ))}
              </div>
            )}
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Historial clínico</div>
            <div className="text-slate-300 mt-2 text-sm space-y-1">
              {patient.previous_injuries && <div><span className="text-slate-500">Lesões anteriores: </span>{patient.previous_injuries}</div>}
              {patient.surgeries && <div><span className="text-slate-500">Cirurgias: </span>{patient.surgeries}</div>}
              {patient.chronic_conditions && <div><span className="text-slate-500">Condições crónicas: </span>{patient.chronic_conditions}</div>}
              {patient.medication && <div><span className="text-slate-500">Medicação: </span>{patient.medication}</div>}
              {patient.lifestyle && <div><span className="text-slate-500">Estilo de vida: </span>{patient.lifestyle}</div>}
              {!patient.previous_injuries && !patient.surgeries && !patient.chronic_conditions &&
                !patient.medication && !patient.lifestyle && !patient.clinical_history && (
                  <span className="text-slate-500">Sem registo. <Link to={`/patients/${id}/intake`} className="text-sky-400 hover:underline">Preencher anamnese</Link>.</span>
                )}
              {patient.clinical_history && !patient.previous_injuries && (
                <div className="text-slate-300 whitespace-pre-wrap">{patient.clinical_history}</div>
              )}
            </div>
          </div>
        </div>

        {/* Vitais & Perfil expandido — mostra apenas se houver pelo menos um campo preenchido */}
        {(patient.height_cm || patient.weight_kg || patient.bmi || patient.birth_date ||
          patient.dominant_side || patient.physical_activity_level || patient.sleep_quality ||
          patient.stress_level || patient.hydration_level || patient.occupation_type) && (
          <div className="mt-6 pt-6 border-t border-slate-800" data-testid="vitals-display">
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-3">Vitais & Perfil</div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
              {patient.birth_date && (
                <div><div className="text-slate-500 text-xs">Nascimento</div><div className="text-slate-200">{patient.birth_date}</div></div>
              )}
              {patient.height_cm && (
                <div><div className="text-slate-500 text-xs">Altura</div><div className="text-slate-200">{patient.height_cm} cm</div></div>
              )}
              {patient.weight_kg && (
                <div><div className="text-slate-500 text-xs">Peso</div><div className="text-slate-200">{patient.weight_kg} kg</div></div>
              )}
              {patient.bmi && (
                <div><div className="text-slate-500 text-xs">IMC</div><div className="text-sky-300 font-medium" data-testid="bmi-value">{patient.bmi}</div></div>
              )}
              {patient.dominant_side && (
                <div><div className="text-slate-500 text-xs">Lateralidade</div><div className="text-slate-200 capitalize">{patient.dominant_side}</div></div>
              )}
              {patient.physical_activity_level && (
                <div><div className="text-slate-500 text-xs">Atividade física</div><div className="text-slate-200 capitalize">{patient.physical_activity_level}</div></div>
              )}
              {patient.sleep_quality && (
                <div><div className="text-slate-500 text-xs">Sono</div><div className="text-slate-200 capitalize">{patient.sleep_quality}</div></div>
              )}
              {patient.stress_level && (
                <div><div className="text-slate-500 text-xs">Stress</div><div className="text-slate-200 capitalize">{patient.stress_level}</div></div>
              )}
              {patient.hydration_level && (
                <div><div className="text-slate-500 text-xs">Hidratação</div><div className="text-slate-200 capitalize">{patient.hydration_level}</div></div>
              )}
              {patient.occupation_type && (
                <div><div className="text-slate-500 text-xs">Ocupação</div><div className="text-slate-200 capitalize">{(patient.occupation_type || "").replace("_", " ")}</div></div>
              )}
            </div>
          </div>
        )}
      </section>

      <section>
        <h2 className="font-display text-2xl text-slate-50 mb-4">Avaliações anteriores</h2>
        {assessments.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-800 p-10 text-center text-slate-500 bg-slate-900/30">
            Sem avaliações. Inicia a primeira para analisar a postura.
          </div>
        ) : (
          <div className="space-y-3">
            {assessments.map((a) => (
              <Link
                key={a.id}
                to={`/assessments/${a.id}`}
                data-testid={`assessment-row-${a.id}`}
                className="flex items-center justify-between rounded-xl border border-slate-800 bg-slate-900/40 p-5 hover:border-sky-500/40 hover:bg-slate-900/70 transition"
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-sky-500/10 border border-sky-500/30 flex items-center justify-center">
                    <FileText className="w-4 h-4 text-sky-400" strokeWidth={1.5} />
                  </div>
                  <div>
                    <div className="text-slate-50 font-medium">Avaliação #{a.id.slice(0,6)}</div>
                    <div className="text-xs text-slate-500">{new Date(a.created_at).toLocaleString("pt-PT")}</div>
                  </div>
                </div>
                <div className="text-sm">
                  {a.analysis?.global_score ? (
                    <span className="text-sky-300">Pontuação {a.analysis.global_score}/100</span>
                  ) : (
                    <span className="text-slate-500">Por analisar</span>
                  )}
                </div>
              </Link>
            ))}
          </div>
        )}
        {hasMore && <LoadMoreButton testId="assessments-load-more" onClick={loadMore} loading={loadingMore} />}
      </section>
      {user?.mio_sign_enabled && <PatientDocuments patientId={patient.id} onStatus={setSignStatus} />}
    </div>
  );
}
